export type GapClassification = 'missing' | 'partial' | 'unclassified';

export const classificationLabel = (classification: GapClassification): string => {
  switch (classification) {
    case 'partial':
      return 'Partial: route exists, parts missing';
    case 'unclassified':
      return 'Unclassified: not in the route classification';
    default:
      return 'Missing: no New UI route yet';
  }
};

export const PAGE_GAP_SENTENCE =
  'This page is still in the legacy view. Open it there to carry on, or copy the gap details to report it.';

// Labels are singular or plural ("Email Status tab", "Title and gender fields").
export const partGapSentence = (label: string, hasLegacyPage: boolean): string =>
  hasLegacyPage
    ? `${label}: not in the New UI yet. Open the legacy page to use this part.`
    : `${label}: not in the New UI yet.`;

const humanize = (word: string): string => word.replace(/_/g, ' ');

const capitalize = (text: string): string => text.charAt(0).toUpperCase() + text.slice(1);

const singularize = (word: string): string => {
  if (word.endsWith('ies')) return `${word.slice(0, -3)}y`;
  if (/(ss|us|x|ch|sh)es$/.test(word)) return word.slice(0, -2);
  if (word.endsWith('s') && !/(ss|us|is)$/.test(word)) return word.slice(0, -1);
  return word;
};

// Gap title when the legacy action set no page_title, e.g. `admin/jobs#show` -> "Job".
export const fallbackPageTitle = (railsAction: string): string => {
  const [controllerPath = '', action = ''] = railsAction.split('#');
  const resource = humanize(controllerPath.split('/').pop() ?? '');
  if (!resource) return 'Untitled page';

  switch (action) {
    case 'index':
      return capitalize(resource);
    case 'show':
      return capitalize(singularize(resource));
    case 'new':
    case 'create':
      return `New ${singularize(resource)}`;
    case 'edit':
    case 'update':
      return `Edit ${singularize(resource)}`;
    default:
      return action ? `${capitalize(resource)}: ${humanize(action)}` : capitalize(resource);
  }
};

export const gapPageTitle = (pageTitle: string, railsAction: string): string =>
  pageTitle.trim() || fallbackPageTitle(railsAction);

interface GapDetailsInput {
  pageTitle: string;
  railsAction: string;
  classification: GapClassification;
  href: string;
  partId?: string;
  partLabel?: string;
}

// Plain-text ticket template for the weekly gap triage (UIUX tickets keyed by railsAction).
export const gapTicketTemplate = ({
  classification,
  href,
  pageTitle,
  partId,
  partLabel,
  railsAction
}: GapDetailsInput): string =>
  [
    `Single-root gap: ${partLabel ?? pageTitle} (${railsAction})`,
    '',
    `Rails action: ${railsAction}`,
    `Page title: ${pageTitle}`,
    ...(partId ? [`Part: ${partId}${partLabel ? ` (${partLabel})` : ''}`] : []),
    `Classification: ${classificationLabel(classification)}`,
    `Example URL path: ${href}`,
    '',
    'Definition of done:',
    '- route admitted (RootQueryProvider, navigation compatible, prop parity fixture)',
    '- parity covers listed, or missing parts declared',
    '- Cypress smoke test passes',
    '- client_routes.json and route_parity.json regenerated; single_root:check passes',
    '- gap hits drop to zero the following week'
  ].join('\n');
